import { type FC, useMemo } from 'react';
import {
  SizedCell,
  SortableHeadCell,
  TextEllipsis,
  TextEllipsisWithTooltip,
} from '__components/Kizen/Table';
import { useTranslation } from 'react-i18next';
import DateTimeInputInline from '__components/Inputs/inline/DateTimeInput';
import ActionCell from '__components/Tables/ActionCell';
import { KizenTypography } from '__app/typography';
import { ONLINE_SOURCE_PAGES_TABLE_ACTIONS } from 'pages/KnowledgeSettings/constants';
import type { OnlineSourcePageListItem } from '@kizen/api/knowledge';
import { getOnlineSourcePageStatusesConfig } from 'pages/KnowledgeSettings/helpers';
import { useTooltip } from '__components/Kizen/Tooltip';

const EMPTY_VALUE = '—';

const ControlsCell: FC<{
  data?: OnlineSourcePageListItem;
  canWrite?: boolean;
  onSelectAction: (
    action: { value: keyof typeof ONLINE_SOURCE_PAGES_TABLE_ACTIONS },
    data: OnlineSourcePageListItem
  ) => void;
}> = ({ data, canWrite, onSelectAction, ...props }) => {
  const { t } = useTranslation();
  const options = useMemo(
    () =>
      [
        canWrite &&
          data?.knowledge_source?.is_active &&
          (data.knowledge_source.is_active_by_user
            ? {
                label: t('Deactivate'),
                value: ONLINE_SOURCE_PAGES_TABLE_ACTIONS.deactivate,
              }
            : {
                label: t('Activate'),
                value: ONLINE_SOURCE_PAGES_TABLE_ACTIONS.activate,
              }),
        {
          label: t('View Content'),
          value: ONLINE_SOURCE_PAGES_TABLE_ACTIONS.view,
        },
      ].filter(Boolean),
    [canWrite, data, t]
  );
  return (
    <ActionCell
      title={t('Select Action')}
      menuType="absolute"
      options={options}
      onSelectAction={onSelectAction}
      {...props}
      data={data}
      width="15px"
      padding="15px"
    />
  );
};

const URLCell: FC<{
  url: OnlineSourcePageListItem['url'];
}> = ({ url }) => {
  const [tooltipProps, tooltip] = useTooltip(
    {
      label: url || EMPTY_VALUE,
    },
    () => true
  );

  const urlObject = url ? new URL(url) : null;
  return (
    <TextEllipsis
      {...tooltipProps}
      type="anchor"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {tooltip}
      {urlObject ? urlObject.pathname + (urlObject.search || '') : EMPTY_VALUE}
    </TextEllipsis>
  );
};

const StatusCell: FC<{
  knowledgeSource: OnlineSourcePageListItem['knowledge_source'];
}> = ({ knowledgeSource }) => {
  const { t } = useTranslation();
  const { label = EMPTY_VALUE, color = null } = useMemo(
    () =>
      knowledgeSource
        ? getOnlineSourcePageStatusesConfig(t)[
            knowledgeSource.is_active && knowledgeSource.is_active_by_user
              ? 'active'
              : knowledgeSource.is_active
                ? 'disabled'
                : 'disabled_by_source'
          ] || {}
        : {},
    [t, knowledgeSource]
  );
  return (
    <TextEllipsisWithTooltip color={color}>{label}</TextEllipsisWithTooltip>
  );
};

type UseColumnsProps = {
  onSelectAction: (
    action: {
      value: keyof typeof ONLINE_SOURCE_PAGES_TABLE_ACTIONS;
    },
    data: OnlineSourcePageListItem
  ) => void;
  canWrite?: boolean;
};

export const useColumns = ({ onSelectAction, canWrite }: UseColumnsProps) => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        id: 'spacer-start',
        headCell: <SizedCell width="15px" />,
        cell: <SizedCell width="15px" />,
        format: () => <span />,
      },
      {
        id: 'page_title',
        headCell: (
          <SortableHeadCell
            label={t('Page Title')}
            width="230px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="230px" padding="30px" />,
        format: (_: never, data: OnlineSourcePageListItem) => {
          return (
            <TextEllipsisWithTooltip>
              {data.knowledge_source?.description || EMPTY_VALUE}
            </TextEllipsisWithTooltip>
          );
        },
      },
      {
        id: 'crawl_config_description',
        headCell: (
          <SortableHeadCell label={t('Source')} width="180px" padding="30px" />
        ),
        cell: <SizedCell width="180px" padding="30px" />,
        format: (_: never, data: OnlineSourcePageListItem) => {
          return (
            <TextEllipsisWithTooltip>
              {data.crawl_config?.description || EMPTY_VALUE}
            </TextEllipsisWithTooltip>
          );
        },
      },
      {
        id: 'url',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('URL')}
            width="230px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="230px" padding="30px" />,
        format: (url: OnlineSourcePageListItem['url']) => {
          return <URLCell url={url} />;
        },
      },
      {
        id: 'is_active_by_user',
        headCell: (
          <SortableHeadCell label={t('Status')} width="130px" padding="30px" />
        ),
        cell: <SizedCell width="130px" padding="30px" />,
        format: (_: never, data: OnlineSourcePageListItem) => {
          return <StatusCell knowledgeSource={data.knowledge_source} />;
        },
      },
      {
        id: 'first_seen',
        headCell: (
          <SortableHeadCell
            numeric
            label={t('First Seen')}
            width="155px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="155px" padding="30px" />,
        format: (firstSeen: OnlineSourcePageListItem['first_seen']) => {
          return firstSeen ? (
            <DateTimeInputInline readOnly value={firstSeen} showDateTooltip />
          ) : (
            <KizenTypography>{EMPTY_VALUE}</KizenTypography>
          );
        },
      },
      {
        id: 'last_seen',
        headCell: (
          <SortableHeadCell
            numeric
            label={t('Last Seen')}
            width="155px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="155px" padding="30px" />,
        format: (_: never, data: OnlineSourcePageListItem) => {
          return data.created ? (
            <DateTimeInputInline
              readOnly
              value={data.created}
              showDateTooltip
            />
          ) : (
            <KizenTypography>{EMPTY_VALUE}</KizenTypography>
          );
        },
      },
      {
        id: 'actions',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Actions')}
            width="70px"
            padding="15px"
            align="right"
          />
        ),
        cell: <ControlsCell onSelectAction={onSelectAction} canWrite={canWrite} />,
      },
    ],
    [onSelectAction, canWrite, t]
  );
};
